import React from 'react';
import { subscribeToNetworkChange, isNetworkAvailable } from 'owa-offline';
import { trace } from 'owa-trace';

type NetworkTransitionType = 'none' | 'offline-to-online' | 'online-to-offline';

interface NetworkState {
    isOnline: boolean;
    wasOnlinePreviously: boolean;
    transitionType: NetworkTransitionType;
    hasOfflineAttempt: boolean;
}

export interface UseNetworkRetryOptions {
    enabled: boolean;
    composeId: string;
    isInitialized: () => boolean;
    onRetrySignal?: () => void; // Callback to handle retry signal
}

export interface UseNetworkRetryReturn {
    networkState: NetworkState;
    isOffline: boolean;
    hasOfflineAttempt: boolean;
}

/**
 * Custom hook for managing network-aware retry signaling
 *
 * This hook provides network state management and calls the onRetrySignal
 * callback when a retry should be attempted. This avoids setState issues
 * by using a callback pattern instead.
 *
 * @param options - Configuration options for the network retry behavior
 * @returns Network state information
 */
export function useNetworkRetry({
    enabled,
    composeId,
    isInitialized,
    onRetrySignal,
}: UseNetworkRetryOptions): UseNetworkRetryReturn {
    // Enhanced network state tracking with explicit transitions
    const networkStateRef = React.useRef<NetworkState>({
        isOnline: isNetworkAvailable(),
        wasOnlinePreviously: isNetworkAvailable(),
        transitionType: 'none',
        hasOfflineAttempt: false,
    });

    // Stable ref for the callback
    const onRetrySignalRef = React.useRef(onRetrySignal);
    onRetrySignalRef.current = onRetrySignal;

    /**
     * Updates network transition state and returns whether a retry should be signaled
     */
    const updateNetworkState = React.useCallback(
        (isOnline: boolean): boolean => {
            const state = networkStateRef.current;

            // Update transition tracking
            state.wasOnlinePreviously = state.isOnline;
            state.isOnline = isOnline;

            // Determine transition type
            if (!state.wasOnlinePreviously && state.isOnline) {
                state.transitionType = 'offline-to-online';
            } else if (state.wasOnlinePreviously && !state.isOnline) {
                state.transitionType = 'online-to-offline';
            } else {
                state.transitionType = 'none';
            }

            // Track offline attempts
            if (!isOnline) {
                state.hasOfflineAttempt = true;
            }

            // Return whether we should signal retry
            return state.transitionType === 'offline-to-online' && !isInitialized();
        },
        [isInitialized]
    );

    /**
     * Network change subscription with retry signaling
     */
    React.useEffect(() => {
        if (!enabled) {
            return;
        }

        const unsubscribe = subscribeToNetworkChange((isOnline: boolean) => {
            trace.info(
                `ExtAddins: ComposeAddins: Network status changed\n` +
                    `isOnline: ${isOnline},\n` +
                    `composeId: ${composeId},\n` +
                    `initialized: ${isInitialized()}`
            );

            // Check if we should signal a retry
            const shouldSignalRetry = updateNetworkState(isOnline);

            if (shouldSignalRetry) {
                trace.info(
                    `ExtAddins: ComposeAddins: Signaling retry\n` + `composeId: ${composeId}`
                );

                // Call the retry signal callback - safe in subscription callback
                onRetrySignalRef.current?.();
            }
        });

        return unsubscribe;
    }, [enabled, composeId, updateNetworkState, isInitialized]);

    return {
        networkState: networkStateRef.current,
        isOffline: !networkStateRef.current.isOnline,
        hasOfflineAttempt: networkStateRef.current.hasOfflineAttempt,
    };
}
